import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Option } from 'src/components/AutoCompleteSelectBox';
import { useFirebaseAuth } from 'src/contexts/AuthContext/FirebaseAuthContext';
import useAuthedRequest from 'src/hooks/useAuthedRequest';
import { Organization } from 'src/interfaces/organization.interface';

type UseOrganizationSwitcherOptions = {
  returnTo: string;
};

export const useOrganizationSwitcher = ({
  returnTo,
}: UseOrganizationSwitcherOptions) => {
  const { get } = useAuthedRequest();
  const user = useFirebaseAuth();
  const navigate = useNavigate();

  const [orgsLoading, setOrgsLoading] = useState(false);
  const [organizations, setOrganizations] = useState<Organization[]>([]);

  const appUserId = user?.appUserId;

  const currentOrg: Option | undefined =
    user?.organization?.id != null
      ? {
          value: user.organization.id ?? '',
          label: user.organization.name ?? '',
        }
      : undefined;

  // Fetch organizations for the user
  const fetchOrganizations = useCallback(async () => {
    if (!appUserId) return;

    setOrgsLoading(true);
    try {
      const organizations: Organization[] = await get(
        `/users/${appUserId}/organizations`,
      );
      setOrganizations(organizations);
    } finally {
      setOrgsLoading(false);
    }
  }, [appUserId, get]);

  useEffect(() => {
    fetchOrganizations();
  }, [fetchOrganizations]);

  // Handler for organization change
  const handleChangeOrganization = (orgId: string) => {
    if (!user) return;

    navigate(`/org-switch?orgId=${orgId}&returnTo=${returnTo}`);
  };

  // Fetch organizations options for autocomplete
  const getOrganizationOptions = async (query: string) => {
    return organizations
      .filter((org) => org.name.toLowerCase().includes(query.toLowerCase()))
      .map((org) => ({
        label: org.name,
        value: org.id,
      }));
  };

  return {
    organizations,
    currentOrg,
    orgsLoading,
    getOrganizationOptions,
    handleChangeOrganization,
    refetchOrganizations: fetchOrganizations,
  };
};
